import { CourseBatchData } from "@utils/interface/journey.interface";

/* A student's course_batch is an M2M, so every row already ships the student's full
   batch list. That's enough to work out which of those batches repeats a course the
   student already took — no per-batch flag needed from the backend. The backend still
   owns the `is_repeat` *filter*, since paging can't be done on a derived value. */

/** `course` may arrive nested or as a bare pk depending on the serializer */
const getCourseKey = (item: CourseBatchData): string | null => {
  const course = (item as any)?.course;
  if (course === null || course === undefined) return null;

  if (typeof course === "object") {
    const key = course.id ?? course.title;
    return key === null || key === undefined ? null : String(key);
  }
  return String(course);
};

/** decides which batch of a course came first; undated batches sort last */
const getStartedAt = (item: CourseBatchData): number => {
  const parsed = item?.start_date ? new Date(item.start_date).getTime() : NaN;
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

/**
 * Batches that repeat a course the student already took. The earliest batch of a
 * course is the original; every later one is a repeat and carries no invoice.
 * Batches with no resolvable course are never flagged — they can't be grouped.
 */
const getRepeatBatchIds = (courseBatch?: CourseBatchData[]): Set<number> => {
  const byCourse = new Map<string, CourseBatchData[]>();

  (courseBatch ?? []).forEach((item) => {
    const key = getCourseKey(item);
    if (!key) return;
    byCourse.set(key, [...(byCourse.get(key) ?? []), item]);
  });

  const repeats = new Set<number>();
  byCourse.forEach((items) => {
    if (items.length < 2) return;
    items
      .slice()
      .sort((a, b) => getStartedAt(a) - getStartedAt(b) || a.id - b.id)
      .slice(1)
      .forEach((item) => repeats.add(item.id));
  });

  return repeats;
};

/** true when any one course shows up in more than one of the student's batches */
const hasRepeatBatch = (courseBatch?: CourseBatchData[]): boolean =>
  getRepeatBatchIds(courseBatch).size > 0;

export { getRepeatBatchIds, hasRepeatBatch };
